import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Wrench, MapPin, Building, X } from 'lucide-react';
import Select from 'react-select';
import api from '../../services/api';
import { toast } from 'react-toastify';
import styles from './SettingsPage.module.css';
import commonStyles from '../../styles/common.module.css';

const ServiceCenterList = () => {
  const [serviceCenters, setServiceCenters] = useState([]);
  const [formData, setFormData] = useState({ name: '', city_id: null, address: '' });
  const [cities, setCities] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServiceCenters();
    fetchCities();
  }, []);

  const fetchServiceCenters = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/service-centers/');
      setServiceCenters(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки сервисных центров');
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await api.get('/settings/cities/');
      setCities(response.data.map(city => ({ 
        value: city.id, 
        label: city.name,
        region: city.region 
      })));
    } catch (error) {
      toast.error('Ошибка загрузки городов');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = { ...formData, city_id: formData.city_id?.value };
    try {
      if (editingId) {
        await api.put(`/settings/service-centers/${editingId}/`, data);
        toast.success('Сервисный центр обновлён');
      } else {
        await api.post('/settings/service-centers/', data);
        toast.success('Сервисный центр создан');
      }
      closeModal();
      fetchServiceCenters();
    } catch (error) {
      toast.error('Ошибка сохранения сервисного центра');
    }
  };

  const handleEdit = (sc) => {
    setFormData({
      name: sc.name,
      city_id: cities.find(c => c.value === sc.city_id) || null,
      address: sc.address || '',
    });
    setEditingId(sc.id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить сервисный центр?')) {
      try {
        await api.delete(`/settings/service-centers/${id}/`);
        toast.success('Сервисный центр удалён');
        fetchServiceCenters();
      } catch (error) {
        toast.error('Ошибка удаления сервисного центра');
      }
    }
  };

  const openAddModal = () => {
    setFormData({ name: '', city_id: null, address: '' });
    setEditingId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({ name: '', city_id: null, address: '' });
    setEditingId(null);
  };

  const getCityName = (cityId) => {
    const city = cities.find(c => c.value === cityId);
    return city ? city.label : '—';
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Загрузка сервисных центров...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h3>
          <Wrench size={24} style={{ marginRight: '0.5rem', color: '#0f766e' }} />
          Сервисные центры
        </h3>
        <button className={styles.addButton} onClick={openAddModal}>
          <Plus size={18} />
          Добавить сервисный центр
        </button>
      </div>

      <div className={styles.tableWrapper}>
        {serviceCenters.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <Wrench size={48} />
            </div>
            <h3>Нет сервисных центров</h3>
            <p>Добавьте первый сервисный центр для начала работы</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Название</th>
                <th>Город</th>
                <th>Адрес</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {serviceCenters.map(sc => (
                <tr key={sc.id} className={styles.clickableRow} onClick={() => handleEdit(sc)}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Wrench size={16} style={{ color: '#0f766e' }} />
                      <strong>{sc.name}</strong>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <MapPin size={14} style={{ color: '#64748b' }} />
                      {getCityName(sc.city_id)}
                    </div>
                  </td>
                  <td>{sc.address || '—'}</td>
                  <td>
                    <button
                      className={styles.actionButton}
                      onClick={(event) => { event.stopPropagation(); handleDelete(sc.id); }}
                      title="Удалить"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <Wrench size={20} />
                {editingId ? 'Редактировать сервисный центр' : 'Добавить сервисный центр'}
              </h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>
                    <Building size={16} />
                    Название сервисного центра
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Введите название сервисного центра"
                    required
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label>
                    <MapPin size={16} />
                    Город
                  </label>
                  <div className={styles.select}>
                    <Select
                      options={cities}
                      value={formData.city_id}
                      onChange={(selected) => setFormData({ ...formData, city_id: selected })}
                      placeholder="Выберите город..."
                      formatOptionLabel={(option) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <MapPin size={14} style={{ color: '#0f766e' }} />
                          <span>{option.label}</span>
                          {option.region && (
                            <span style={{ color: '#64748b', fontSize: '0.8125rem' }}>
                              ({option.region})
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label>
                    <Building size={16} />
                    Адрес
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Введите адрес сервисного центра"
                  />
                </div>
                
                <button type="submit" className={styles.submitButton}>
                  {editingId ? 'Обновить сервисный центр' : 'Создать сервисный центр'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceCenterList;
